import { useRef } from "react";

type GlobalSearchBarProps = Readonly<{
    value: string;
    onChange: (value: string) => void;
    onSubmit?: (value: string) => void;
    placeholder?: string;
}>;

export function GlobalSearchBar({
    value,
    onChange,
    onSubmit,
    placeholder = "Search by SM#, patient, patient ID, postal, phone…",
}: GlobalSearchBarProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    const handleClear = () => {
        onChange("");
        onSubmit?.("");
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            handleClear();
        }
        if (e.key === "Enter") {
            onSubmit?.(value);
        }
    };

    return (
        <div
            style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                width: 260,
                minWidth: 180,
                flexShrink: 1,
            }}
        >
            {/* Search icon */}
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "var(--text-muted)",
                }}
            >
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
                <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>

            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                aria-label="Global search"
                autoComplete="off"
                spellCheck={false}
                style={{
                    width: "100%",
                    height: 38,
                    padding: "0 34px 0 36px",
                    border: "1.5px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 14,
                    color: "var(--text-dark)",
                    background: "var(--white)",
                    outline: "none",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                }}
                onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--primary-blue)";
                    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0, 102, 179, 0.12)";
                }}
                onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--border)";
                    e.currentTarget.style.boxShadow = "none";
                }}
            />

            {/* Clear button */}
            {value && (
                <button
                    type="button"
                    onClick={handleClear}
                    aria-label="Clear search"
                    style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 4,
                        borderRadius: 4,
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <line x1="3" y1="3" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <line x1="11" y1="3" x2="3" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>
            )}
        </div>
    );
}
